import { useCallback } from 'react';
import { useStore } from '../contexts/StoreContext';
import { hasPermission } from '../lib/permissions';

export function usePermissions() {
  const { currentStore, permissions, role } = useStore();

  const can = useCallback(
    (permission: string): boolean => {
      // Owner always has full access.
      if (role === 'owner') return true;
      // Store not loaded yet → allow briefly to avoid a permission-denied flash;
      // the backend enforces every write regardless. This MUST gate on the store
      // not being loaded, NOT on empty permissions: a loaded employee with no
      // permissions is a real "deny all", not a loading state. (Previously this
      // returned true whenever role was falsy AND permissions were empty, which
      // silently granted no-permission employees full UI access.)
      if (!currentStore) return true;
      return hasPermission(permissions, permission);
    },
    [currentStore, permissions, role],
  );

  const canAny = useCallback(
    (...perms: string[]): boolean => {
      return perms.some((p) => can(p));
    },
    [can],
  );

  const canAll = useCallback(
    (...perms: string[]): boolean => {
      return perms.every((p) => can(p));
    },
    [can],
  );

  return { can, canAny, canAll, role, isOwner: role === 'owner' };
}
